import React from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { FONT_FAMILY } from '../common/sharedFunctions';
import {
  buildDefaultSupportMenu,
  normalizeSupportMenuItems,
} from 'common/src/other/supportMenu';

const ACTION_OPTIONS = [
  { value: 'url', labelKey: 'support_action_url', fallback: 'Abrir enlace (URL)' },
  { value: 'phone', labelKey: 'support_action_phone', fallback: 'Llamar teléfono' },
  { value: 'email', labelKey: 'support_action_email', fallback: 'Enviar correo' },
  { value: 'screen', labelKey: 'support_action_screen', fallback: 'Pantalla de la app' },
  { value: 'content', labelKey: 'support_action_content', fallback: 'Contenido editable' },
];

export default function SupportMenuEditor({ data, setData, classes, isRTL }) {
  const { t } = useTranslation();
  const items = normalizeSupportMenuItems(data?.supportMenuItems);
  const displayItems = items.length > 0 ? items : buildDefaultSupportMenu(data || {});

  const updateItems = (nextItems) => {
    setData({ ...data, supportMenuItems: nextItems });
  };

  const updateRow = (index, patch) => {
    const next = displayItems.map((row, i) => (i === index ? { ...row, ...patch } : row));
    updateItems(next);
  };

  const removeRow = (index) => {
    updateItems(displayItems.filter((_, i) => i !== index));
  };

  const addRow = () => {
    updateItems([
      ...displayItems,
      {
        id: `custom_${Date.now()}`,
        label: t('support_new_item', 'Nuevo enlace'),
        action: 'url',
        value: '',
        enabled: true,
      },
    ]);
  };

  const resetDefaults = () => {
    updateItems(buildDefaultSupportMenu(data || {}));
  };

  const fieldClass = isRTL === 'rtl' ? classes.rootRtl : classes.textField;

  return (
    <Grid container spacing={2} style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr', marginTop: 10 }}>
      <Grid item xs={12}>
        <Typography component="h2" variant="h5" style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>
          {t('support_menu_title', 'Menú de soporte y cumplimiento legal')}
        </Typography>
        <Typography
          variant="body2"
          style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left', opacity: 0.85, marginTop: 4, fontFamily: FONT_FAMILY }}
        >
          {t(
            'support_menu_hint',
            'Configura los enlaces que ven los usuarios en Soporte (app). Puedes activar, renombrar y cambiar destinos. Cumplimiento Colombia: PQR, términos, privacidad, SIC.'
          )}
        </Typography>
      </Grid>

      {displayItems.map((row, index) => (
        <Grid item xs={12} key={row.id || index}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={row.enabled !== false}
                    onChange={(e) => updateRow(index, { enabled: e.target.checked })}
                    color="primary"
                  />
                }
                label={t('active', 'Activo')}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                className={fieldClass}
                variant="outlined"
                margin="dense"
                fullWidth
                label={t('support_item_label', 'Texto visible')}
                value={row.label}
                onChange={(e) => updateRow(index, { label: e.target.value })}
                InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                className={fieldClass}
                select
                variant="outlined"
                margin="dense"
                fullWidth
                label={t('support_item_action', 'Acción')}
                value={row.action}
                onChange={(e) => updateRow(index, { action: e.target.value })}
                InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
              >
                {ACTION_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {t(opt.labelKey, opt.fallback)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                className={fieldClass}
                variant="outlined"
                margin="dense"
                fullWidth
                label={t('support_item_value', 'Destino (URL, pantalla o contenido)')}
                value={row.value}
                onChange={(e) => updateRow(index, { value: e.target.value })}
                helperText={t(
                  'support_item_value_hint',
                  'Ej: Complain, About, terms, privacy, dataAuth, faq, https://wa.me/...'
                )}
                InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
              />
            </Grid>
            <Grid item xs={12} sm={1}>
              <IconButton onClick={() => removeRow(index)} aria-label="delete">
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
      ))}

      <Grid item xs={12}>
        <Button startIcon={<AddIcon />} onClick={addRow} variant="outlined" style={{ marginRight: 8 }}>
          {t('support_add_item', 'Agregar enlace')}
        </Button>
        <Button onClick={resetDefaults} variant="text">
          {t('support_reset_defaults', 'Restaurar menú Colombia')}
        </Button>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          className={fieldClass}
          variant="outlined"
          margin="normal"
          fullWidth
          id="supportChatUrl"
          label={t('support_chat_url', 'Enlace chat / WhatsApp')}
          name="supportChatUrl"
          onChange={(e) => setData({ ...data, supportChatUrl: e.target.value })}
          value={data.supportChatUrl || ''}
          helperText={t('support_chat_url_hint', 'Usado por defecto en «Chatea con nosotros» si no hay URL en el ítem.')}
          InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
        />
        <TextField
          className={fieldClass}
          variant="outlined"
          margin="normal"
          fullWidth
          id="sicLogoUrl"
          label={t('sic_logo_url', 'Logo Superintendencia (URL imagen)')}
          name="sicLogoUrl"
          onChange={(e) => setData({ ...data, sicLogoUrl: e.target.value })}
          value={data.sicLogoUrl || ''}
          InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
        />
        <TextField
          className={fieldClass}
          variant="outlined"
          margin="normal"
          fullWidth
          id="sicWebsiteUrl"
          label={t('sic_website_url', 'Enlace Superintendencia de Industria y Comercio')}
          name="sicWebsiteUrl"
          onChange={(e) => setData({ ...data, sicWebsiteUrl: e.target.value })}
          value={data.sicWebsiteUrl || ''}
          InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data.supportShowSic !== false}
              onChange={(e) => setData({ ...data, supportShowSic: e.target.checked })}
              color="primary"
            />
          }
          label={t('support_show_sic', 'Mostrar logo SIC en la app')}
        />
      </Grid>
    </Grid>
  );
}
